/*
 * BHS branding for the /bjc/ pages. Like /js/bjc-assignments.js, this is a
 * website asset loaded by every BJC page's chrome (the content repo's
 * bjc/book/chrome.html, with a root-absolute src — the book only ever
 * serves from this origin). It adds to the navbar:
 *
 *   - leftmost: the "Berkeley High Computer Science" wordmark (styled by
 *     .bhs-wordmark in the book's frozen theme CSS, vendor/_static), linking
 *     back to the site root
 *   - rightmost: the logged-in user's username from /whoami ("not logged in"
 *     when anonymous), so you can tell at a glance whose session this is
 *
 * (The body.bjc-dark class below is the book chrome's dark-mode state —
 * content/books/bjc/chrome.html sets it alongside the
 * html[data-theme] hook its stylesheets key on. Until 2026-10-17 it was
 * named quarto-dark, from the Quarto render the theme was frozen from;
 * the old name is still accepted so this can deploy ahead of the content
 * publish that renames it — drop it once the published book carries
 * bjc-dark.)
 */

import { apiFetch } from './modules/fetch-helpers.ts';

declare global {
  interface Window {
    // The BJC chrome's own dark-mode toggle (an inline script in its head);
    // absent on any page that doesn't carry the chrome.
    bjcToggleColorScheme?: () => boolean;
  }
}

function brand(): void {
  const container = document.querySelector('nav.navbar .navbar-container');
  if (!container) return;

  const wordmark = document.createElement('a');
  wordmark.className = 'bhs-wordmark';
  wordmark.href = '/';
  wordmark.textContent = 'Berkeley High Computer Science';
  container.prepend(wordmark);

  const who = document.createElement('span');
  who.className = 'bhs-username';
  container.append(who);

  void apiFetch('/whoami')
    .then(async (r) => {
      if (r.status !== 401 && !r.ok) {
        // A server error is not "not logged in".
        who.textContent = 'sign-in check failed';
        return;
      }
      const data = (await r.json()) as {
        userId?: string;
        username?: string | null;
        google?: { email?: string };
      };
      who.textContent =
        r.ok && data.userId
          ? (data.username ?? data.google?.email ?? data.userId)
          : 'not logged in';
    })
    .catch(() => {
      // Unreachable server: say so rather than claiming "not logged in".
      who.textContent = 'sign-in check failed';
    });
}

/*
 * Theme bridge (plans/dark-mode.md): the BJC chrome has its own dark mode —
 * an inline head script (stored under localStorage `bjc-dark-mode` for the
 * standalone preview; on the website it reads our resolved value back)
 * that sets body.bjc-dark, toggled by window.bjcToggleColorScheme().
 * The rest of the site keys on the theme-boot contract: BJC's effective
 * theme is its own override (the profile page's BJC row, theme:bjc), else
 * the site preference. Bridge the two:
 *
 *   - preferences → book: whenever BJC's effective theme disagrees with the
 *     body's bjc-dark class, drive BJC's own toggle so its own state and
 *     its stored key stay consistent (on load, and again whenever
 *     the bjc surface's effective value changes — profile edits in another
 *     tab, OS flips while following System).
 *
 *   - book → preferences: a click on BJC's navbar toggle is an explicit
 *     choice for THIS book, so wrap it to write the bjc override (never the
 *     site theme; the profile page's "Use the Site setting everywhere"
 *     clears it back to following). setOverride() fires the surface event,
 *     whose re-sync then agrees (no loop: sync only acts on disagreement).
 */
function bridgeTheme(): void {
  const { bhsTheme, bjcToggleColorScheme } = window;
  if (!bhsTheme || !bjcToggleColorScheme) return; // chrome without one half — nothing to bridge

  const bookIsDark = () =>
    document.body.classList.contains('bjc-dark') || document.body.classList.contains('quarto-dark'); // pre-2026-10-17 chrome, see above

  const sync = () => {
    const wantDark = bhsTheme.resolvedFor('bjc') === 'dark';
    if (bookIsDark() !== wantDark) {
      bjcToggleColorScheme();
    }
  };

  window.bjcToggleColorScheme = () => {
    bjcToggleColorScheme();
    bhsTheme.setOverride('bjc', bookIsDark() ? 'dark' : 'light');
    return false;
  };

  document.addEventListener('bhs:surfacethemechange', (e) => {
    if ((e as CustomEvent<{ surface: string }>).detail.surface === 'bjc') sync();
  });
  sync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    brand();
    bridgeTheme();
  });
} else {
  brand();
  bridgeTheme();
}
